import type {
  BrowserCommentTarget,
  BrowserRenderMode,
  BrowserViewport,
} from "@cmmd-forge/contracts";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  capturePointToViewportPoint,
  resolveAnnotationAnchor,
  resolveBrowserCaptureLayout,
  resolveBrowserCanvasCommitTarget,
  resolveBrowserCanvasDraftRect,
  resolveBrowserCanvasSelection,
  viewportPointToCapturePoint,
  viewportRectToCaptureRect,
  type BrowserCaptureLayout,
} from "./BrowserCanvas.logic";

interface BrowserCanvasProps {
  readonly url: string | null;
  readonly tabId: string | null;
  readonly renderMode: BrowserRenderMode | null;
  readonly screenshotDataUrl: string | undefined;
  readonly viewport: BrowserViewport | undefined;
  readonly captureId: string | undefined;
  readonly capturedAt: string | undefined;
  readonly selectionEnabled: boolean;
  readonly selectedTarget: BrowserCommentTarget | null;
  readonly onTargetChange: (target: BrowserCommentTarget | null) => void;
  readonly annotationNote?: string;
  readonly onAnnotationNoteChange?: (note: string) => void;
  readonly onAnnotationSubmit?: () => void;
  readonly onAnnotationCancel?: () => void;
  readonly onContainerResize?: (size: { width: number; height: number }) => void;
}

interface DragPoint {
  readonly x: number;
  readonly y: number;
}

export function BrowserCanvas(props: BrowserCanvasProps) {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const noteInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [dragStart, setDragStart] = useState<DragPoint | null>(null);
  const [dragCurrent, setDragCurrent] = useState<DragPoint | null>(null);
  const [surfaceSize, setSurfaceSize] = useState({ width: 0, height: 0 });
  const { onContainerResize } = props;

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) {
      return;
    }
    const updateSize = () => {
      const bounds = surface.getBoundingClientRect();
      const size = { width: bounds.width, height: bounds.height };
      setSurfaceSize(size);
      onContainerResize?.(size);
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [onContainerResize]);

  const draftRect = useMemo(
    () => resolveBrowserCanvasDraftRect({ start: dragStart, current: dragCurrent }),
    [dragCurrent, dragStart],
  );

  const { rect: selectionRect, point: selectionPoint } = resolveBrowserCanvasSelection({
    selectedTarget: props.selectedTarget,
    url: props.url,
    draftRect,
  });
  const captureLayout = useMemo(
    () =>
      props.screenshotDataUrl
        ? resolveBrowserCaptureLayout({
            containerWidth: surfaceSize.width,
            containerHeight: surfaceSize.height,
            viewport: props.viewport,
          })
        : null,
    [props.screenshotDataUrl, props.viewport, surfaceSize.height, surfaceSize.width],
  );
  const renderedSelectionRect =
    selectionRect && captureLayout
      ? viewportRectToCaptureRect({ rect: selectionRect, layout: captureLayout })
      : selectionRect;
  const renderedSelectionPoint =
    selectionPoint && captureLayout
      ? viewportPointToCapturePoint({ point: selectionPoint, layout: captureLayout })
      : selectionPoint;

  const normalizePoint = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = surfaceRef.current?.getBoundingClientRect();
    if (!bounds) {
      return null;
    }
    if (props.viewport && captureLayout) {
      return capturePointToViewportPoint({
        point: {
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        },
        layout: captureLayout,
        viewport: props.viewport,
      });
    }
    return {
      x: Math.max(0, Math.round(event.clientX - bounds.left)),
      y: Math.max(0, Math.round(event.clientY - bounds.top)),
    } satisfies DragPoint;
  };

  const commitSelection = (point: DragPoint, rect: BrowserCommentTarget["rect"]) => {
    if (!props.url || !props.tabId) {
      return;
    }
    props.onTargetChange({
      tabId: props.tabId,
      url: props.url,
      ...(props.captureId ? { captureId: props.captureId } : {}),
      ...(props.capturedAt ? { capturedAt: props.capturedAt } : {}),
      ...(props.viewport ? { viewport: props.viewport } : {}),
      ...(rect ? { rect } : { point }),
    });
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!props.selectionEnabled) {
      return;
    }
    const point = normalizePoint(event);
    if (!point) {
      return;
    }
    setDragStart(point);
    setDragCurrent(point);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!props.selectionEnabled || !dragStart) {
      return;
    }
    const point = normalizePoint(event);
    if (!point) {
      return;
    }
    setDragCurrent(point);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!props.selectionEnabled || !dragStart) {
      return;
    }
    const point = normalizePoint(event);
    if (!point) {
      setDragStart(null);
      setDragCurrent(null);
      return;
    }

    const target = resolveBrowserCanvasCommitTarget({ point, dragStart });
    commitSelection(target.point, target.rect);
    setDragStart(null);
    setDragCurrent(null);
  };

  const annotationAnchor = resolveAnnotationAnchor({
    layout: captureLayout,
    point: renderedSelectionPoint,
    rect: renderedSelectionRect,
  });

  useEffect(() => {
    if (props.selectedTarget && props.selectionEnabled) {
      noteInputRef.current?.focus();
    }
  }, [props.selectedTarget, props.selectionEnabled]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-background" ref={surfaceRef}>
      {props.selectionEnabled ? (
        <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full border border-primary/30 bg-background/90 px-3 py-1 text-xs font-medium text-foreground shadow-sm">
          Select a page area, then add a note
        </div>
      ) : null}
      {props.renderMode === "capture" && props.screenshotDataUrl ? (
        <BrowserCaptureImage
          dataUrl={props.screenshotDataUrl}
          layout={captureLayout}
          viewport={props.viewport}
        />
      ) : (
        <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {props.url
            ? "No browser capture available for this tab yet."
            : "Open a page to start the browser preview."}
        </div>
      )}

      <div
        className={
          props.selectionEnabled
            ? "absolute inset-0 cursor-crosshair"
            : "pointer-events-none absolute inset-0"
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {renderedSelectionRect ? (
          <div
            className="absolute rounded-sm border border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--background)/0.75)]"
            style={{
              left: renderedSelectionRect.x,
              top: renderedSelectionRect.y,
              width: renderedSelectionRect.width,
              height: renderedSelectionRect.height,
            }}
          />
        ) : null}
        {renderedSelectionPoint ? (
          <span
            aria-hidden="true"
            className="absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-background shadow-[0_0_0_3px_hsl(var(--primary)/0.2)]"
            style={{ left: renderedSelectionPoint.x, top: renderedSelectionPoint.y }}
          />
        ) : null}
      </div>
      {props.selectedTarget && annotationAnchor ? (
        <div
          className="absolute z-10 w-72 max-w-[calc(100%-1rem)] rounded-lg border border-border bg-card p-2 shadow-lg"
          style={{
            left: annotationAnchor.left,
            top: annotationAnchor.top,
            transform: annotationAnchor.transform,
          }}
        >
          <label className="sr-only" htmlFor="browser-annotation-note">
            Browser annotation
          </label>
          <textarea
            id="browser-annotation-note"
            ref={noteInputRef}
            value={props.annotationNote ?? ""}
            onChange={(event) => props.onAnnotationNoteChange?.(event.target.value)}
            placeholder="Describe what should change here."
            className="min-h-20 w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring"
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={props.onAnnotationCancel}
              className="inline-flex h-7 items-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={props.onAnnotationSubmit}
              disabled={(props.annotationNote ?? "").trim().length === 0}
              className="inline-flex h-7 items-center rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-opacity disabled:pointer-events-none disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BrowserCaptureImage(props: {
  readonly dataUrl: string;
  readonly layout: BrowserCaptureLayout | null;
  readonly viewport: BrowserViewport | undefined;
}) {
  if (!props.layout || !props.viewport) {
    return (
      <img
        src={props.dataUrl}
        alt="Browser preview capture"
        className="h-full w-full object-fill"
      />
    );
  }

  return (
    <div
      className="absolute overflow-hidden bg-background"
      style={{
        left: props.layout.left,
        top: props.layout.top,
        width: props.layout.width,
        height: props.layout.height,
      }}
    >
      <img
        src={props.dataUrl}
        alt="Browser preview capture"
        className="h-full w-full object-fill"
      />
    </div>
  );
}
